import React, { useEffect, useMemo, useState } from 'react';
import { BusinessBanksDropdown, ErrorHeader } from '../../components';
import { ListPayout, ListPayoutGroup } from '../../AdminClasses';
import { addCommas, bindChange, bindClick, fromUnix, fromUnixLocal, getNow, getNowLocal, toggleArray } from '@arema/components/Util';
import { Link, useNavigate } from 'react-router-dom';
import { SetLoading } from '@arema/components/Classes';
import { useTitle } from '@arema/components/Hooks';
import { useUser } from '../../AdminHooks';
import { Button, Field, Header, Label, Message, Pagination, Table, Modal, Icon, Stat, Input } from 'react-frontier2';
import { useTranslation } from 'react-i18next';
import { firstBy } from 'thenby';
import API from '../../API';
import classNames from 'classnames';
import UserAccess from '../../UserAccess';
import Validator from '@arema/components/Validator';
import { useFrontier } from 'react-frontier2';

const PAYOUT_COUNT = 20;
const TABLE_WIDTH = 700;

enum ShownModal{
	VIEW_PAYOUT = 1,
	SELECT_PAYOUTS = 2,
	CONFIRM_PAYOUT = 10,
	REJECT_PAYOUT = 11,
	COMPLETE_PENDING = 20,
}

enum SelectType{
	APPROVAL = 1,
	REJECT = 2,
	GROUP = 10,
}

interface PayoutChanges{
	payout_id: number,
	data: { [x: string]: any },
}

var PayoutList = ()=>{
	var navigate = useNavigate();
	var { t } = useTranslation();
	var { user, hasAccess } = useUser();
	var { setTitle } = useTitle();
	var { Toast } = useFrontier();
	var [pendingPayouts, setPendingPayouts] = useState<ListPayout[]>(null);
	var [completedPayouts, setCompletedPayouts] = useState<ListPayout[]>(null);
	var [approvalPayouts, setApprovalPayouts] = useState<ListPayout[]>(null);
	var [pendingGroups, setPendingGroups] = useState<ListPayoutGroup[]>(null);
	var [completedGroups, setCompletedGroups] = useState<ListPayoutGroup[]>(null);
	var [shownModal, setShownModal] = useState<ShownModal>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [viewingPayout, setViewingPayout] = useState<number>(null);
	var [selectType, setSelectType] = useState<SelectType>(null);
	var [selectedPayouts, setSelectedPayouts] = useState<number[]>([]);
	var [selectedBank, setSelectedBank] = useState<number>(null);
	var [scheduledDate, setScheduledDate] = useState<Date>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [loadingStp, setLoadingStp] = useState<boolean>(false);
	var [rejectFeedback, setRejectFeedback] = useState<string>('');
	var [page, setPage] = useState<number>(0);
	var [groupsPage, setGroupsPage] = useState<number>(0);
	var [pageLoading, setPageLoading] = useState<boolean>(false);
	var [groupsPageLoading, setGroupsPageLoading] = useState<boolean>(false);
	// var [completeForm, setCompleteForm] = useState<{
	// 	stp: boolean,
	// 	completed: boolean
	// }>(null);

	var ACCESS = useMemo(()=>({
		approve: hasAccess(UserAccess.CONTABILIDAD.APPROVE_PAYOUT),
		complete: hasAccess(UserAccess.CONTABILIDAD.SET_PAYOUT_COMPLETE),
		stp: hasAccess(UserAccess.CONTABILIDAD.DOWNLOAD_STP_FILE),
		create_group: hasAccess(UserAccess.CONTABILIDAD.CREATE_PAYOUT_GROUP),
	}), [user]);

	useEffect(()=>{
		setTitle('Liquidaciones');
		if(completedPayouts===null){
			setLoadError(null);
			API.getPayouts(true, true, true, true, true, 0, PAYOUT_COUNT).then(res=>{
				if(res.error) return setLoadError(res.message);
				setPendingPayouts(res.data.pending);
				setApprovalPayouts(res.data.approval);
				setCompletedPayouts(res.data.completed);
				setPendingGroups(res.data.groups?.pending || []);
				setCompletedGroups(res.data.groups?.completed || []);
			}).catch(err=>{
				setLoadError('Hubo un error inesperado cargando las liquidaciones (LCL-1)');
			})
		}
	}, []);

	var pending_groups_dates = useMemo(()=>{
		var groups : {
			date: number,
			groups: typeof pendingGroups
		}[] = [];
		if(!pendingGroups) return [];
		for(var i of pendingGroups){
			var dt = fromUnixLocal(i.date_scheduled)
			var ix = groups.findIndex(a=>{
				if(i.date_scheduled===null) return a.date===null;
				var grdt = fromUnixLocal(a.date);
				return grdt.hasSame(dt, 'day');
			});
			if(ix==-1){
				groups.push({
					date: i.date_scheduled,
					groups: [i]
				});
			}else{
				groups[ix].groups.push(i);
			}
		}
		return groups;
	}, [pendingGroups]);

	var {
		pending_dates,
		pending_total,
	} = useMemo(()=>{
		var dates : {
			date: number,
			payouts: typeof pendingPayouts
		}[] = [];
		if(!pendingPayouts) return {
			pending_dates: dates,
			pending_total: 0,
		};
		var total = 0;
		for(var i of pendingPayouts){
			total += i.amount;
			var dt = fromUnixLocal(i.date_scheduled)
			var ix = dates.findIndex(a=>{
				if(i.date_scheduled===null) return a.date===null;
				var grdt = fromUnixLocal(a.date);
				return grdt.hasSame(dt, 'day');
			});
			if(ix==-1){
				dates.push({
					date: i.date_scheduled,
					payouts: [i]
				});
			}else{
				dates[ix].payouts.push(i);
			}
		}
		return {
			pending_dates: dates,
			pending_total: total,
		};
	}, [pendingPayouts]);

	var now = useMemo(()=>{
		return getNowLocal();
	}, []);

	var onPayoutsPageChange = (new_page: number)=>{
		setPage(new_page);
		setPageLoading(true);
		API.getPayouts(true, false, false, true, false, new_page*PAYOUT_COUNT, PAYOUT_COUNT).then(res=>{
			if(res.error) return setLoadError(res.message);
			setCompletedPayouts(res.data.completed);
		}).catch(err=>{
			setLoadError('Hubo un error inesperado cargando las liquidaciones (LCL-2)');
		}).finally(()=>{
			setPageLoading(false);
		})
	}

	var onGroupsPageChange = (new_page: number)=>{
		setGroupsPage(new_page);
		setGroupsPageLoading(true);
		API.getPayouts(false, false, false, true, true, new_page*PAYOUT_COUNT, PAYOUT_COUNT).then(res=>{
			if(res.error) return setLoadError(res.message);
			setCompletedGroups(res.data.groups?.completed);
		}).catch(err=>{
			setLoadError('Hubo un error inesperado cargando las liquidaciones (LCL-2)');
		}).finally(()=>{
			setGroupsPageLoading(false);
		})
	}

	var showConfirm = (payout_id: number, type: SelectType)=>{
		return ()=>{
			// setCompleteForm({ stp: false, completed: false });
			setViewingPayout(payout_id);
			setSelectType(type);
			setModalPrompts(null);
			setRejectFeedback('');
			setShownModal(ShownModal.CONFIRM_PAYOUT);
		}
	}

	var showPayout = (payout_id: number)=>{
		return ()=>{
			setViewingPayout(payout_id);
			setShownModal(ShownModal.VIEW_PAYOUT);
		}
	}

	var updatePayouts = (payouts: PayoutChanges[])=>{
		var all_payouts = [...approvalPayouts, ...completedPayouts, ...pendingPayouts];
		for(var i of payouts){
			var ix = all_payouts.findIndex(a=>a.payout_id==i.payout_id);
			if(ix==-1) return;
			all_payouts[ix] = {
				...all_payouts[ix],
				...i.data
			};
		}
		setPendingPayouts(all_payouts.filter(a=>a.approved && !a.completed && !a.rejected));
		setApprovalPayouts(all_payouts.filter(a=>!a.approved && !a.completed && !a.rejected));
		setCompletedPayouts(all_payouts.filter(a=>a.completed || a.rejected));
	}

	var sendApprove = (payouts: number[])=>{
		return (setLoading: SetLoading)=>{
			if(!ACCESS.approve){
				setShownModal(null);
				return Toast.error('No tienes acceso a aprobar liquidaciones');	
			}
			setLoading(true);
			API.approvePayouts(payouts).then(res=>{
				if(res.error) return Toast.error(res.message);
				Toast.success('Se ha aprobado la liquidación');
				var payout_changes: PayoutChanges[] = [];
				for(let i of res.data.approved){
					payout_changes.push({ payout_id: i, data: { approved: true }})
				}
				for(let i of res.data.completed){
					payout_changes.push({ payout_id: i, data: { approved: true, completed: true }});
				}

				updatePayouts(payout_changes);
				setShownModal(null);
			}).catch(err=>{
				Toast.error('Hubo un error inesperado aprobando las liquidaciónes (LCL-1)');
			}).finally(()=>{
				setLoading(false);
			})
		}
	}
	
	var sendGroup = (payouts: number[])=>{
		return (setLoading: SetLoading)=>{
			if(!ACCESS.create_group){
				setShownModal(null);
				return Toast.error('No tienes acceso a agrupar liquidaciones');
			}
			var { valid, prompts } = Validator({selectedBank, scheduledDate }, {
				selectedBank: [{ rule: 'number', prompt: 'Favor de seleccionar el banco' }],
				scheduledDate: [{ rule: 'empty', prompt: 'Favor de seleccionar la fecha agendada' }],
			})
			setModalPrompts(prompts);
			if(!valid) return;

			setLoading(true);
			API.createPayoutGroup(payouts, selectedBank, Math.floor(scheduledDate.getTime()/1000)).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				if(res.data.group_id){
					navigate(`/payouts/group/${res.data.group_id}`);
				}else{
					window.location.reload();
				}
			}).catch(err=>{
				setModalPrompts(['Hubo un error inesperado creando el grupo de liquidaciones (LCL-1)']);
			}).finally(()=>{
				setLoading(false);
			})
		}
	}

	var sendReject = (payouts: number[])=>{
		return (setLoading: SetLoading)=>{
			if(!ACCESS.approve){
				setShownModal(null);
				return Toast.error('No tienes acceso a aprobar o rechazar liquidaciones.');
			}
			if((!rejectFeedback || rejectFeedback.length<5)){
				return setModalPrompts(['Favor de ingresar feedback para rechazar la liquidación.']);
			}
			setModalPrompts(null);
			setLoading(true);
			API.rejectPayouts(payouts, rejectFeedback).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				Toast.success('Se han rechazado las liquidaciones.');
				updatePayouts(payouts.map(a=>({ payout_id: a, data: { rejected: true } })));
				setShownModal(null);
			}).catch(err=>{
				setModalPrompts(['Hubo un error inesperado rechazando la liquidación (LCL-1)']);
			}).finally(()=>{
				setLoading(false);
			})
		}
	}

	var selectPayouts = (select_type: SelectType)=>{
		return ()=>{
			setSelectedPayouts([]);
			setSelectedBank(null);
			setScheduledDate(null);
			setSelectType(select_type);
			setModalPrompts(null);
			setShownModal(ShownModal.SELECT_PAYOUTS);
		}
	}

	var sendSelection = (setLoading: SetLoading)=>{
		setModalPrompts(null);
		if(selectType===SelectType.APPROVAL){
			return sendApprove(selectedPayouts)(setLoading);
		}else if(selectType===SelectType.GROUP){
			return sendGroup(selectedPayouts)(setLoading);
		}
	}
	
	var all_payouts = useMemo(()=>{
		return [...(approvalPayouts || []), ...(completedPayouts || []), ...(pendingPayouts || [])];
	}, [approvalPayouts, completedPayouts, pendingPayouts]);
	var shown_payout = useMemo(()=>{
		if(!viewingPayout) return null;
		return all_payouts.find(a=>a.payout_id==viewingPayout);
	}, [viewingPayout, all_payouts])
	var selected_total = useMemo(()=>{
		var total = 0;
		if(!selectedPayouts || selectedPayouts.length==0) return total;
		for(var i of selectedPayouts){
			var p = all_payouts.find(a=>a.payout_id===i);
			if(!p) continue;
			total += p.amount;
		}
		return total;
	}, [selectedPayouts, all_payouts]);

	var closeModal = bindClick(setShownModal);
	var select_text = selectType===SelectType.APPROVAL ? 'Aprobar' : (selectType===SelectType.REJECT ? 'Rechazar' : 'UNKWN');
	var select_status = selectType===SelectType.APPROVAL ? 'aprobada' : (selectType===SelectType.REJECT ? 'rechazada' : 'UNKWN');
	var shown_modal_payouts = (selectType===SelectType.APPROVAL ? approvalPayouts : (selectType===SelectType.GROUP ? pendingPayouts : []));
	
	if(loadError){
		return <ErrorHeader error={loadError} />
	}
	if(completedPayouts===null){
		return <Header loading text='Cargando liquidaciones' />
	}

	var RejectOptions = selectType===SelectType.REJECT && ACCESS.approve && <>
		<Field label='Feedback' style={{ marginTop: 15 }}>
			<textarea onChange={bindChange(setRejectFeedback)} value={rejectFeedback} placeholder='Comentarios sobre la razón para rechazar la liquidación' rows={4}></textarea>
		</Field>
	</>
	
	return <div>
		<Header text='Liquidaciones' />
		{pendingGroups.length>0 && (
			<Table striped style={{ maxWidth: TABLE_WIDTH, margin: 'auto', marginBottom: 15 }}>
				<Table.Head
					title='Grupos de liquidaciones'
					data={[['ID', 'Banco', 'Estatus', 'Fecha', 'Liquidaciones', 'Total']]}
					collapsingIndexes={[0, 2, 3, 4, 5]}
					centeredIndexes={[5]}
				/>
				<Table.Body>
					{pending_groups_dates.length>1 && (
						<Table.Row>
							<Table.Cell colSpan={4} style={{ fontWeight: 'bold' }}>Total</Table.Cell>
							<Table.Cell centered>{addCommas(pendingGroups.length, false)}</Table.Cell>
							<Table.Cell centered>${addCommas(pendingGroups.reduce((a,b)=>a+b.amount, 0))}</Table.Cell>
						</Table.Row>
					)}
					{pending_groups_dates.sort(firstBy('date', 'asc')).map(g=>{
						var dt = fromUnix(g.date);
						var diff = now.diff(dt, 'day').days;
						var today = Math.floor(diff)===0;
						var days = Math.ceil(Math.abs(diff));
						var amount = g.groups.reduce((a,b)=>a+b.amount, 0);
						return (
							<React.Fragment key={`PYGRDT-${g.date}`}>
								<Table.Row header>
									<Table.Cell colSpan={4} style={{ color: 'black', fontSize: 12 }}><>
										{dt.toFormat('dd/MMM/yy')}
										{' '}({today ? t('common.time.today') : t(`common.time.${diff>0 ? 'ago' : 'in'}`, { value: t('common.time.days', { count: days }) })})
									</></Table.Cell>
									<Table.Cell centered style={{ color: 'black', fontSize: 12 }}>{addCommas(g.groups.length, false)}</Table.Cell>
									<Table.Cell centered style={{ color: 'black', fontSize: 12 }}>${addCommas(amount)}</Table.Cell>
								</Table.Row>
								{g.groups.map(a=>(
									<Table.Row compact selectable
										as={Link}
										to={`/payouts/group/${a.group_id}`}
										key={`PYGRP-${a.group_id}`}
										centeredIndexes={[4, 5]}
										collapsingIndexes={[0, 2, 3, 4, 5]}
										data={[
											a.group_id,
											a.bank_name,
											<Label style={{ minWidth: 120 }} color={a.approved ? 'blue' : 'orange'}>
												{a.approved ? 'Pendiente' : 'En aprobación'}
											</Label>,
											<>
												<Icon name='calendar' solid />
												{fromUnix(a.date_scheduled).toFormat('dd/MMM/yy')}
											</>,
											a.payouts,
											`$${addCommas(a.amount)}`
										]} 
									/>
								))}
							</React.Fragment>
						)
					})}
				</Table.Body>
			</Table>	
		)}
		{approvalPayouts.length>0 && (
			<Table style={{ maxWidth: TABLE_WIDTH, margin: 'auto' }}>
				<Table.Head
					title='Aprobación'
					data={[['ID', 'Evento', 'Tipo', 'Cantidad', 'Fecha', '']]}
					collapsingIndexes={[0, 2, 3, 4, 5]}
					titleRight={!!ACCESS.approve && (
						<Button size='tiny' iconName='check-circle' text='Aprobar' color='green' onClick={selectPayouts(SelectType.APPROVAL)} />
					)}
				/>
				<Table.Body
					data={approvalPayouts.map(a=>[
						a.payout_id, 
						a.event_name,
						<Label color='yellow' style={{ minWidth: 100 }}>
							{a.payout_type_name}
						</Label>,
						addCommas(a.amount), 
						<div className={classNames({
							red: a.date_scheduled && fromUnix(a.date_scheduled).hasSame(getNow(), 'day',)
						})}>
							{!!a.date_scheduled && <i className="calendar icon"></i>}
							{fromUnix(a.date_scheduled || a.date_created).toFormat('dd/MMM/yy')}
						</div>,
						<Button size='tiny' text='Opciones' iconRight='caret-down' iconRightStyle={{ marginLeft: 5, marginRight: 0 }} menu={[
							{ text: t('common.approve'), iconName: 'check-circle', onClick: showConfirm(a.payout_id, SelectType.APPROVAL) },
							{ text: t('common.reject'), iconName: 'check-circle', onClick: showConfirm(a.payout_id, SelectType.APPROVAL) },
							{ text: t('payout_list.view_info'), iconName: 'check-circle', onClick: showConfirm(a.payout_id, SelectType.APPROVAL) },
						]} />
					])}
				/>
			</Table>
		)}
		{pendingPayouts.length>0 && (
			<Table style={{ maxWidth: TABLE_WIDTH, margin: 'auto', marginTop: 15, }}>
				<Table.Head
					title='Pendientes'
					data={[['ID', 'Evento', 'Tipo', 'Cantidad', 'Creación']]}
					collapsingIndexes={[0, 2, 3, 4]}
					titleRight={ACCESS.complete && (
						<Button size='tiny' iconName='check-circle' text='Agrupar' color='blue' onClick={selectPayouts(SelectType.GROUP)} />
					)}
				/>
				<Table.Body>
					{pending_dates.length>1 && (
						<Table.Row style={{ fontWeight: 'bold' }}>
							<Table.Cell colSpan={2}>Total</Table.Cell>
							<Table.Cell centered>{pendingPayouts.length}</Table.Cell>
							<Table.Cell>${addCommas(pending_total)}</Table.Cell>
							<Table.Cell></Table.Cell>
						</Table.Row>
					)}
					{pending_dates.sort(firstBy('date', 'asc')).map(d=>{
						var dt = fromUnix(d.date);
						var diff = now.diff(dt, 'day').days;
						var today = Math.floor(diff)===0;
						var days = Math.ceil(Math.abs(diff));
						var amount = d.payouts.reduce((a,b)=>a+b.amount, 0);
						return (
							<React.Fragment key={`PYDTS-${d.date}`}>
								<Table.Row header>
									<Table.Cell colSpan={2} style={{ color: 'black', fontSize: 12 }}>
										{d.date ? <>
											{dt.toFormat('dd/MMM/yy')}
											{' '}({today ? t('common.time.today') : t(`common.time.${diff>0 ? 'ago' : 'in'}`, { value: t('common.time.days', { count: days }) })})
										</> : (
											t('payout_list.not_scheduled')
										)}
									</Table.Cell>
									<Table.Cell centered style={{ color: 'black', fontSize: 12 }}>{addCommas(d.payouts.length, false)}</Table.Cell>
									<Table.Cell centered style={{ color: 'black', fontSize: 12 }}>${addCommas(amount)}</Table.Cell>
									<Table.Cell></Table.Cell>
								</Table.Row>
								{d.payouts.map(a=>(
									<Table.Row compact selectable
									key={`PYTP-${a.payout_id}`}
									collapsingIndexes={[0, 2, 3, 4]}
									as={Link}
									to={`/payouts/${a.payout_id}`}
									data={[
										a.payout_id, 
										a.event_name, 
										<Label color='blue' style={{ minWidth: 100 }}>
											{a.payout_type_name}
										</Label>,
										addCommas(a.amount),
										fromUnixLocal(a.date_created).toFormat('dd/MMM/yy HH:mm')
									]}	
								/>
								))}
							</React.Fragment>
						)
					})}
				</Table.Body>
			</Table>
		)}
		{(completedGroups.length>0 || groupsPage>0) && (
			<Table style={{ maxWidth: TABLE_WIDTH, margin: 'auto', marginTop: 15 }}>
				<Table.Head
					title='Grupos completados'
					data={[['ID', 'Banco', 'Estatus', 'Fecha', 'Liquidaciones', 'Total']]}
					collapsingIndexes={[0, 2, 3, 4, 5]}
				/>
				<Table.Body>
					{!groupsPageLoading ? (
						completedGroups.map(a=>(
							<Table.Row compact selectable
								as={Link}
								to={`/payouts/group/${a.group_id}`}
								key={`PYGRP-${a.group_id}`}
								centeredIndexes={[4]}
								collapsingIndexes={[0, 2, 3, 4, 5]}
								data={[
									a.group_id,
									a.bank_name,
									<Label style={{ minWidth: 120 }} color={'green'}>
										Completado
									</Label>,
									fromUnixLocal(a.date_completed).toFormat('dd/MMM/yy'),
									a.payouts,
									`$${addCommas(a.amount)}`
								]} 
							/>
						))
					) : (
						<Table.Row>
							<Table.Cell colSpan={100}>
								<Header loading text='Cargando grupos...' style={{ margin: '50px 0' }} />
							</Table.Cell>
						</Table.Row>
					)}
				</Table.Body>
				{groupsPage>0 || groupsPageLoading || completedGroups.length>=PAYOUT_COUNT ? (
					<Table.Footer>
						<Table.Cell style={{ textAlign: 'right' }} colSpan={100} row>
							<Pagination inline page={groupsPage} disabled={groupsPageLoading} onPageChange={onGroupsPageChange} hasNext={completedGroups.length>=PAYOUT_COUNT} />
						</Table.Cell>
					</Table.Footer>
				) : null}
			</Table>	
		)}
		<Table style={{ maxWidth: TABLE_WIDTH, margin: 'auto', marginTop: 15 }}>
			<Table.Head
				title='Liquidaciones'
				data={[['ID', 'Evento', 'Cantidad', 'Fecha', 'Estatus']]}
			/>
			<Table.Body>
				{!pageLoading ? (
					(!completedPayouts || completedPayouts.length==0) ? (
						<Table.Row>
							<Table.Cell className='empty' colSpan={5}>
								No hay liquidaciones completadas en el sistema
							</Table.Cell>
						</Table.Row>
					) : completedPayouts.map(a=>(
						<Table.Row 
							compact
							selectable
							collapsingIndexes={[0, 2, 3, 4]} 
							as={Link}
							to={`/payouts/${a.payout_id}`}
							key={`CPLPYT-${a.payout_id}`}
							data={[
								a.payout_id, a.event_name, addCommas(a.amount), fromUnixLocal(a.date_completed || a.date_cancelled || a.date_created).toFormat('dd/MMM/yy HH:mm'),
								<div className={classNames('fr label', {
									green: a.completed && !a.rejected && !a.cancelled,
									red: a.rejected || a.cancelled,
								})}>
									{a.rejected ? 'Rechazada' : a.cancelled ? 'Cancelada' : 'Completada'}
								</div>
							]} 
						/>
					)) 
				) : (
					<Table.Row>
						<Table.Cell colSpan={5}>
							<Header loading text='Cargando liquidaciones...' style={{ margin: '50px 0' }} />
						</Table.Cell>
					</Table.Row>
				)}
			</Table.Body>
			{page>0 || pageLoading || completedPayouts.length==PAYOUT_COUNT ? (
				<Table.Footer>
					<Table.Cell style={{ textAlign: 'right' }} colSpan={100} row>
						<Pagination inline page={page} disabled={pageLoading} onPageChange={onPayoutsPageChange} hasNext={completedPayouts.length>=PAYOUT_COUNT} />
					</Table.Cell>
				</Table.Footer>
			) : null}
		</Table>
		<Modal size='tiny' open={shownModal===ShownModal.CONFIRM_PAYOUT} onClose={closeModal}>
			<Modal.Header>{select_text} liquidación {shown_payout?.payout_id}</Modal.Header>
			{shownModal===ShownModal.CONFIRM_PAYOUT && <Modal.Content>
				<Header text={`¿${select_text} liquidación?`} subtext={`Marcará la liquidación como ${select_status}`} style={{ paddingLeft: 0, paddingRight: 0, fontSize: 26 }} />
				<div style={{ textAlign: 'center' }}>
					{selectType===SelectType.APPROVAL ? (
						(shown_payout.bank_account) ? 'Se agregará a la fila de liquidaciones pendientes a liquidar.' : 'Ya que la liquidación no es por transferencia se agregará a las liquidaciones completadas.'
					) : selectType===SelectType.REJECT ? (
						shown_payout.user_creator===user.user_id ? <>
							Se rechazará la liquidación, ya que tu fuiste el que creó la liquidación <b>no se enviará ningún correo.</b>
						</> : (
							'Se rechazará la liquidación y se le enviará un correo con los comentarios ingresados a el creador de la liquidación.'
						)
					) : null}
				</div>
				<Table details style={{ marginTop: 15 }}>
					<Table.Head
						titleSize='small'
						title='Información'
					/>
					<Table.Body data={[
						['ID', shown_payout.payout_id],
						['Evento', shown_payout.event_name],
						['Cantidad', addCommas(shown_payout.amount)],
						...(shown_payout.date_scheduled ? [
							['Agendada', fromUnix(shown_payout.date_scheduled).toFormat('dd/MMM/yy')]
						] : []),
						['Creador', shown_payout.user_creator_name],
					]}	/>
				</Table>
				{/* {CompleteOptions} */}
				{RejectOptions}
				<Message type='error' list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={closeModal} />
				<Button text={select_text} color={selectType===SelectType.REJECT ? 'red' : 'black'} onClick={!shown_payout ? null : selectType===SelectType.APPROVAL ? sendApprove([shown_payout.payout_id]) : (selectType===SelectType.REJECT ? sendReject([shown_payout.payout_id]) : null)} />
			</Modal.Actions>
		</Modal>
		<Modal size='tiny' open={shownModal===ShownModal.VIEW_PAYOUT} onClose={closeModal}>
			<Modal.Header>Liquidación {shown_payout?.payout_id}</Modal.Header>
			{shownModal===ShownModal.VIEW_PAYOUT && !!shown_payout && <Modal.Content>
				<Table details fitted>
					<Table.Body data={[
						['ID', shown_payout.payout_id],
						['Tipo', shown_payout.payout_type_name],
						['Cantidad', addCommas(shown_payout.amount)],
						['Calendarios', shown_payout.dates],
						['Transferencia', !(shown_payout.clabe || shown_payout.card_number) ? 'Solo registro' : <>
							<i className={classNames('icon', {
								'credit card': !!shown_payout.card_number,
								'building' : !!shown_payout.clabe
							})}></i>
							{shown_payout.bank} **{shown_payout.clabe || shown_payout.card_number}
						</>],
						[],
						['Aprobada', <>
							<i className={classNames("icon", {
								'red remove': !shown_payout.approved && !shown_payout.completed,
								'green check': !!shown_payout.approved || shown_payout.completed
							})}></i> 
							<span className={classNames({
								'red': !shown_payout.approved && !shown_payout.completed,
								'green': !!shown_payout.approved || shown_payout.completed
							})}>
								{(!shown_payout.approved || !!shown_payout.completed) ? 'No aprobada' : 'Aprobada'}
							</span>
						</>],
						['Estatus', <>
							<i className={classNames("icon", {
								'yellow clock': !shown_payout.completed,
								'green check': !!shown_payout.completed
							})}></i> 
							<span className={classNames({
								'yellow': !shown_payout.completed,
								'green': !!shown_payout.completed
							})}>
								{!!shown_payout.completed ? 'Completada' : 'Pendiente'}
							</span>
						</>],
					]} />
				</Table>
				<Button 
					color='black'
					text='Ver información completa'
					as={Link}
					to={`/payouts/${shown_payout.payout_id}`}
					target='_blank'
					style={{ display: 'block', margin: 'auto', width: 250, marginTop: 15 }} 
				/>
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' onClick={closeModal} />
			</Modal.Actions>
		</Modal>
		<Modal size='large' open={shownModal===ShownModal.SELECT_PAYOUTS} onClose={closeModal}>
			<Modal.Header actions={(
				<Button text='Seleccionar todos' size='small' color='blue' onClick={()=>setSelectedPayouts(shown_modal_payouts.map(a=>a.payout_id))} />
			)}>{selectType===SelectType.APPROVAL ? 'Aprobar' : 'Agrupar'} liquidaciones</Modal.Header>
			{shownModal===ShownModal.SELECT_PAYOUTS && <Modal.Content>
				<Table fitted selectable top>
					<Table.Head 
						centeredIndexes={[0]}
						collapsingIndexes={[0, 1, 3, 4, 5]}
						data={[[<Icon name='check' />, 'ID', 'Evento', 'Tipo', 'Cantidad', 'Fecha']]} 
					/>
					<Table.Body>
						{shown_modal_payouts.map(a=>{
							var selected = selectedPayouts.indexOf(a.payout_id)!==-1;
							return <Table.Row
								collapsingIndexes={[0, 1, 3, 4, 5]}
								centeredIndexes={[0]}
								onClick={()=>{
									setSelectedPayouts(toggleArray(selectedPayouts, a.payout_id))
								}}
								data={[
									<Icon name={selected ? 'check-circle' : 'circle'} solid={!!selected} style={{ margin: 0 }} />,
									a.payout_id,
									a.event_name,
									a.payout_type_name,
									<div style={{ fontSize: 16, fontWeight: 'bold', textAlign: 'center' }}>
										{addCommas(a.amount)}
									</div>,
									<div>
										{!!a.date_scheduled && <Icon name="calendar" />}
										{fromUnix(a.date_scheduled || a.date_created).toFormat(`dd/MMM/yy${!!a.date_scheduled ? '' : ' HH:mm'}`)}
									</div>,
								]}
							/>
						})}
					</Table.Body>
				</Table>
				<div style={{ maxWidth: 500, margin: 'auto' }}>
					{/* {CompleteOptions} */}
					{selectType===SelectType.APPROVAL ? (
						<Message style={{ marginTop: 15 }} centered>
							Se agregará a la fila de liquidaciones pendientes a liquidar. Las liquidaciones seleccionadas que son solo por registro (sin transferencia de dinero) se marcarán como completadas. <br />
							<b style={{ fontSize: 20 }}>{selectedPayouts.length} liquidacion{selectedPayouts.length==1 ? '' : 'es'} seleccionada{selectedPayouts.length==1 ? '' : 's'}</b>
						</Message>
					) : selectType===SelectType.GROUP ? <>
						<BusinessBanksDropdown value={selectedBank} onValueChange={setSelectedBank} style={{ marginTop: 15 }} />
						<Input label='Fecha agendada' readonly calendar={{
							date: scheduledDate,
							minDate: now.toJSDate(),
							onSelected: setScheduledDate,
							mode: 'date'
						}} />
						<Message style={{ margin: 'auto', marginTop: 15, maxWidth: 500 }} type='info' centered>
							<Stat value={`$${addCommas(selected_total)}`} label={'Total agrupado'} />
							Se creará un grupo de liquidaciónes que requerirá aprobación para ser liquidado.
						</Message>
					</> : null}
					<Message type='error' list={modalPrompts} />
				</div>
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={closeModal} />
				<Button text='Continuar' color='black' onClick={sendSelection} disabled={!selectedPayouts || selectedPayouts.length==0} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default PayoutList;